import { inject, observer } from 'mobx-react'
import styled from 'styled-components'

import TypographyPro from 'themes/TypographyPro'
import { respondAbove } from 'styles/mixins'

import type _Account from 'mobx/Account'
import type _Application from 'mobx/Application'
import type _Infra from 'mobx/Infra'
import type { AuthenticatorStatusKeys } from 'mobx/Account/Account.type'

import CircleOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import LinearProgress from '@material-ui/core/LinearProgress'
import CircularProgress from '@material-ui/core/CircularProgress'
import { withStyles } from '@material-ui/core/styles'

import { getTranslatedTextByKey } from 'utils/language'
import { QRCodeType } from 'utils/constants'

import IconComponent from 'themes/IconComponent'
import QRCodeCanvas from 'components/common/QRCode'

import useAuthenticator, { AUTHENTICATOR_STATUS } from 'hooks/useAuthenticator'
import { useEffect, useRef } from 'react'

const NO_KIOSK_BOOTSTRAP_KEY = '__no_kiosk__'

const Title = styled(TypographyPro)`
	text-transform: uppercase;
	color: var(--strokeMenuTitle);
`

const StyledInactive = styled(TypographyPro)`
	color: var(--inactive);
`

const StyledScanAgain = styled(TypographyPro)`
	color: var(--err);
	text-align: center;
`

const StyledContainer = styled.div`
	color: var(--fonts);
	padding-inline-start: 20px;
	padding-inline-end: 21px;

	display: flex;
	gap: 28px;
	flex-direction: column;

	${respondAbove.md`
		width: 580px;
		padding: unset;
	`}
`

const StyledContent = styled.div`
	border: solid 1px var(--disable);
	border-radius: var(--borderRadiusFrame);
	padding: 0px 20px 20px 20px;
`

const StyledInnerContent = styled.div`
	gap: 20px;
	display: flex;
	flex-direction: column;
`

const StyledDescription = styled.div`
	display: flex;
	gap: 12px;
	flex-direction: column;
`

const StyledDescriptionBody = styled(TypographyPro)`
	white-space: break-spaces;
`

const StyledConnectionStatus = styled.div<{connected: boolean}>`
	display: flex;
	padding: 8px;
	gap: 8px;
	border-radius: var(--borderRadiusFrame);
	background: ${({ connected }) => (connected ? 'var(--successBg)' : 'var(--optionalBackground)')};
	align-items: center;
	color: ${({ connected }) => (connected ? 'var(--strokeMenuTitle)' : 'var(--inactive)')}; ;
`

const StyledQrFooter = styled.div`
	display: flex;
	gap: 20px;
	flex-direction: column;
	align-items: center;
`

const StyledLinearProgress = withStyles({
	colorPrimary: {
		backgroundColor: 'var(--optionalBackground)',
		borderRadius: 'var(--borderRadiusBtn)',
	},
	bar: {
		backgroundColor: 'var(--primary)',
	},
})(LinearProgress)

const StyledProgressBarContainer = styled.div`
	width: 100%;
	gap: 8px;
	display: flex;
	flex-direction: column;
`

const StyledCircleOutlinedIcon = styled(CircleOutlinedIcon)`
	width: 20.73px;
	height: 20.73px;
`

const StyledCheckCircleIcon = styled(CheckCircleIcon)`
	width: 20.73px;
	height: 20.73px;
	color: var(--approved);
`

const StyledDisconnect = styled.div`
	display: flex;
	gap: 8px;
	margin: auto;
	padding: 12px 20px;
	color: var(--primary);
	align-items: center;
	cursor: pointer;
`

const StyledExitToAppIcon = styled(ExitToAppIcon)`
	width: 16px;
	height: 16px;
`

const StyledStripeContainer = styled.div`
	display: flex;
	padding: 0px 10px 28px 10px;
	width: fit-content;
	margin: auto;
`

const QRCodeContainer = styled.div`
	margin: auto;
	width: 175px;
	height: 175px;
	background: var(--footerAndDarkBackgrounds);
	border-radius: 4px;

	canvas {
		width: 175px !important;
		height: 175px !important;
	}
`

const StyledConnectingBlock = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	padding: 32px 0;
`

const getTranslateTexts = () => ({
	TITLE: getTranslatedTextByKey('eCommerce.accountSettings.authenticator', 'Kiosk connection code'),

	CONNECTED_STATUS: getTranslatedTextByKey('eCommerce.authenticator.connected.status', 'Connected'),
	CONNECTED_TITLE: getTranslatedTextByKey('eCommerce.authenticator.connected.title', `You're all set!`),
	CONNECTED_BODY: getTranslatedTextByKey(
		'eCommerce.authenticator.connected.body',
		`You'll be disconnected automatically once your order is done.\nNeed to disconnect now? Just tap the button below`
	),

	DISCONNECT_ACTION: getTranslatedTextByKey('eCommerce.authenticator.disconnect.action', 'Disconnect'),
	DISCONNECT_CANCEL: getTranslatedTextByKey('eCommerce.authenticator.disconnect.cancel', 'Stay connected'),
	DISCONNECT_TITLE: getTranslatedTextByKey('eCommerce.authenticator.disconnect.title', 'Disconnect from kiosk?'),
	DISCONNECT_MESSAGE: getTranslatedTextByKey(
		'eCommerce.authenticator.disconnect.message',
		`If you're in the middle of an order, you'll lose your coupons and have to start over.`
	),

	DISCONNECTED_ALERT_TITLE: getTranslatedTextByKey('eCommerce.authenticator.disconnected.alert.title', `You have been disconnected from kiosk`),
	DISCONNECTED_ALERT_MESSAGE: getTranslatedTextByKey(
		'eCommerce.authenticator.disconnected.alert.message',
		`The order on kiosk is completed, or the user has decided to disconnect over there`
	),
	DISCONNECTED_ALERT_BUTTON: getTranslatedTextByKey('eCommerce.authenticator.disconnected.alert.button', `Got it!`),

	DISCONNECTED_STATUS: getTranslatedTextByKey('eCommerce.authenticator.disconnected.status', 'Disconnected'),
	DISCONNECTED_BODY: getTranslatedTextByKey(
		'eCommerce.authenticator.disconnected.body',
		'Connect to the kiosk and unlock your coupon wallet!\nScan the QR code displayed below with the kiosk to connect.'
	),

	STATUS_GENERATING: getTranslatedTextByKey('eCommerce.authenticator.status.generating', 'Code is generating...'),
	STATUS_STILL_VALID: getTranslatedTextByKey(
		'eCommerce.authenticator.status.stillValid',
		'This code previously generated is still valid. Please use it now or wait until it expires...'
	),
	STATUS_EXPIRES_IN: (codeExpiresIn: number) =>
		getTranslatedTextByKey('eCommerce.authenticator.status.expiresIn', 'Your code will expire in {{codeExpiresIn}} seconds', { codeExpiresIn }),
	STATUS_EXPIRED: getTranslatedTextByKey('eCommerce.authenticator.status.expired', 'Your code is expired'),

	QR_LOGIN_SCAN_AGAIN: getTranslatedTextByKey(
		'eCommerce.authenticator.qrLogin.scanAgain',
		'Session expired — please rescan the QR on the kiosk.'
	),
})

interface AuthenticatorProps {
	Application: typeof _Application
	Account: typeof _Account
	Infra: typeof _Infra
	qrToken?: string
	patchOk?: boolean
	patchKeys?: AuthenticatorStatusKeys
}

export const Authenticator = inject(
	'Account',
	'Application',
	'Infra'
)(
	observer(({ Application, Account, Infra, qrToken, patchOk, patchKeys }: AuthenticatorProps) => {
		// Pass the PATCH state through as a tri-state. `undefined` = the page hasn't resolved
		// the PATCH yet, and the hook must NOT treat that as "PATCH failed" (which is what the
		// previous `patchOk === true` collapse to boolean caused).
		const kioskQrBootstrap = qrToken
			? { qrToken, patchOk, keys: patchKeys }
			: undefined

		const {
			isConnected,
			isConnecting,
			connectionState,
			progress,
			unknownExpirationTime,
			codeExpiresIn,
			scanAgain,
			start,
			disconnect,
		} = useAuthenticator(Application, Account, () => { informDisconnection() }, kioskQrBootstrap)

		// (Re)bootstrap on every meaningful change to (qrToken, patchOk).
		//
		// We track the *last token we bootstrapped on* rather than a one-shot boolean so we
		// re-run `init()` when a new `qrToken` arrives while the component is still mounted
		// (the user revisited a different deeplink, the kiosk replaced its session after a
		// previous order, etc.). A naive boolean here would leave the hook polling the old
		// keys forever, eventually hitting the EXPIRED arm and clearing the QR to a black
		// square.
		//
		// Sentinel `NO_KIOSK_BOOTSTRAP_KEY` represents the regular (no-kiosk) entry path so the
		// initial mount in that flow still triggers exactly once.
		//
		// We still defer until `patchOk` is a definitive boolean for kiosk flows: bootstrapping
		// before that resolves would race the page-level PATCH and we'd commit to the wrong
		// branch.
		const lastBootstrapKeyRef = useRef<string | undefined>(undefined)
		useEffect(() => {
			if (qrToken && patchOk === undefined) return
			const currentKey = qrToken ?? NO_KIOSK_BOOTSTRAP_KEY
			if (currentKey === lastBootstrapKeyRef.current) return
			lastBootstrapKeyRef.current = currentKey
			start()
		}, [qrToken, patchOk])

		const confirmDisconnect = () => {
			Infra.setNotification({
				open: true,
				title: getTranslateTexts().DISCONNECT_TITLE,
				message: getTranslateTexts().DISCONNECT_MESSAGE,
				okAction: Infra.closeNotification,
				okText: getTranslateTexts().DISCONNECT_CANCEL,
				cancelText: getTranslateTexts().DISCONNECT_ACTION,
				customAction: () => {
					Infra.closeNotification()
					disconnect()
				},
				customButtonType: 'secondary',
				closable: false,
			})
		}

		const informDisconnection = () => {
			Infra.setNotification({
				open: true,
				title: getTranslateTexts().DISCONNECTED_ALERT_TITLE,
				message: getTranslateTexts().DISCONNECTED_ALERT_MESSAGE,
				okText: getTranslateTexts().DISCONNECTED_ALERT_BUTTON,
				okAction: () => {
					Infra.closeNotification()
					start()
				},
				onClose: () => {
					Infra.closeNotification()
					start()
				},
				cancelAction: undefined,
				closable: false,
			})
		}

		// While the kiosk-initiated PATCH is in flight or the kiosk hasn't completed the
		// connect action yet, we render a dedicated loader instead of the QR.
		const showConnectingLoader = isConnecting || (qrToken && patchOk === undefined)

		return (
			<StyledContainer>
				<Title variant="h2">{getTranslateTexts().TITLE}</Title>
				<StyledContent>
					<StyledStripeContainer>
            			<IconComponent asset="/icons/footerDecoration.svg" height="1.25rem" />
					</StyledStripeContainer>
					<StyledInnerContent>
						<StyledConnectionStatus connected={isConnected}>
							{isConnected ? <StyledCheckCircleIcon /> : <StyledCircleOutlinedIcon />}
							<TypographyPro variant="Subhead">
								{isConnected ? getTranslateTexts().CONNECTED_STATUS : getTranslateTexts().DISCONNECTED_STATUS}
							</TypographyPro>
						</StyledConnectionStatus>
						<StyledDescription>
							{isConnected && (
								<div>
									<TypographyPro variant="BodySmallHighlighted">{getTranslateTexts().CONNECTED_TITLE}</TypographyPro>
									<StyledDescriptionBody variant="BodySmall">{getTranslateTexts().CONNECTED_BODY}</StyledDescriptionBody>
								</div>
							)}
							{!isConnected && !showConnectingLoader && (
								<StyledDescriptionBody variant="BodySmall">{getTranslateTexts().DISCONNECTED_BODY}</StyledDescriptionBody>
							)}
						</StyledDescription>

						{isConnected && (
							<StyledDisconnect onClick={confirmDisconnect}>
								<StyledExitToAppIcon />
								<TypographyPro variant="CTATextBig">{getTranslateTexts().DISCONNECT_ACTION}</TypographyPro>
							</StyledDisconnect>
						)}

						{!isConnected && showConnectingLoader && (
							<StyledConnectingBlock>
								<CircularProgress />
							</StyledConnectingBlock>
						)}

						{!isConnected && !showConnectingLoader && (
							<>
								<QRCodeContainer>
									<QRCodeCanvas content={connectionState.code ? {type: QRCodeType.Authenticator, data: {code: connectionState.code}}: undefined} />
								</QRCodeContainer>
								<StyledQrFooter>
									{scanAgain && (
										<StyledScanAgain variant="BodySmall">{getTranslateTexts().QR_LOGIN_SCAN_AGAIN}</StyledScanAgain>
									)}
									<StyledProgressBarContainer>
										<StyledLinearProgress
											variant={connectionState.code && !unknownExpirationTime ? 'determinate' : undefined}
											value={connectionState.code && !unknownExpirationTime ? progress : undefined}
										/>

										{!connectionState.code && (
											<StyledInactive variant="BodySmall">{getTranslateTexts().STATUS_GENERATING}</StyledInactive>
										)}

										{connectionState.code && unknownExpirationTime && (
											<StyledInactive variant="BodySmall">{getTranslateTexts().STATUS_STILL_VALID}</StyledInactive>
										)}

										{connectionState.code && !unknownExpirationTime && codeExpiresIn > 0 && (
											<StyledInactive variant="BodySmall">{getTranslateTexts().STATUS_EXPIRES_IN(codeExpiresIn)}</StyledInactive>
										)}

										{connectionState.code && !unknownExpirationTime && codeExpiresIn === 0 && (
											<StyledInactive variant="BodySmall">{getTranslateTexts().STATUS_EXPIRED}</StyledInactive>
										)}
									</StyledProgressBarContainer>
								</StyledQrFooter>
							</>
						)}
					</StyledInnerContent>
				</StyledContent>
			</StyledContainer>
		)
	})
)
